<template>
  <div class="members-panel">
    <!-- Header -->
    <header class="page-header">
      <div class="header-content">
        <div class="title-row">
          <h1>店铺管理与组织架构</h1>
          <span class="org-badge">租户 ID: 1 · 店铺管理 (ID: 74)</span>
        </div>
        <p class="header-desc">
          全平台共 {{ (organizationTree?.groups || []).length }} 个业务团队 ·
          {{ totalStoreCount }} 家营业店铺 ·
          当前选中: <strong>{{ currentStore.name }}</strong>
        </p>
      </div>
      <div class="header-actions">
        <button
          type="button"
          class="btn-refresh"
          :disabled="loading"
          @click="refreshTree"
        >
          <RefreshCw :size="14" :class="{ spinning: loading }" />
          <span>刷新架构树</span>
        </button>
      </div>
    </header>

    <!-- Stats Metric Cards -->
    <OrgStatsHeader
      :org-tree="organizationTree"
      :total-groups="(organizationTree?.groups || []).length"
      :total-stores="totalStoreCount"
    />

    <!-- Tree Explorer Section -->
    <section class="org-tree-card">
      <div class="tree-toolbar">
        <div class="search-wrap">
          <Search :size="14" class="search-icon" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="搜索团队、负责人、店铺名称或店铺ID…"
            class="tree-search-input"
          />
          <button v-if="searchQuery" type="button" class="btn-clear" @click="searchQuery = ''">
            <X :size="12" />
          </button>
        </div>

        <div class="tree-actions">
          <button type="button" class="btn-toggle-all" @click="toggleAll">
            {{ isAllExpanded ? '全部收起' : '全部展开' }}
          </button>
        </div>
      </div>

      <!-- Groups & Stores Tree Grid -->
      <div class="tree-grid-container">
        <BaseEmpty
          v-if="!filteredGroups.length"
          title="未找到匹配的团队或店铺"
          description="请尝试更换关键字搜索"
        />

        <OrgTeamCard
          v-for="group in filteredGroups"
          :key="group.id"
          :group="group"
          :is-expanded="expandedIds.has(group.id)"
          :current-store-id="session.storeId"
          @toggle-expand="toggleGroup"
          @switch-store="handleSelectStore"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { RefreshCw, Search, X } from '@lucide/vue';
import { usePlatformStore } from '@/composables/usePlatformStore';
import OrgStatsHeader from '@/components/platform/members/OrgStatsHeader.vue';
import OrgTeamCard from '@/components/platform/members/OrgTeamCard.vue';
import BaseEmpty from '@/components/common/BaseEmpty.vue';

const {
  session,
  currentStore,
  organizationTree,
  loadOrganizationStores,
  switchStore,
  showToast,
} = usePlatformStore();

const loading = ref(false);
const searchQuery = ref('');
const expandedIds = ref(new Set());

const totalStoreCount = computed(() => {
  const groups = organizationTree.value?.groups || [];
  return groups.reduce((sum, g) => sum + (g.stores?.length || 0), 0);
});

const filteredGroups = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  const groups = organizationTree.value?.groups || [];
  if (!q) return groups;

  return groups.map((g) => {
    const groupMatches = g.name.toLowerCase().includes(q) || String(g.id).includes(q);
    const matchingStores = (g.stores || []).filter(s =>
      s.name.toLowerCase().includes(q) || String(s.departmentId || s.id).includes(q) || groupMatches
    );
    if (!matchingStores.length && !groupMatches) return null;
    return {
      ...g,
      stores: matchingStores.length ? matchingStores : g.stores,
    };
  }).filter(Boolean);
});

const isAllExpanded = computed(() => {
  const groups = organizationTree.value?.groups || [];
  return groups.length > 0 && groups.every(g => expandedIds.value.has(g.id));
});

function toggleGroup(id) {
  if (expandedIds.value.has(id)) {
    expandedIds.value.delete(id);
  } else {
    expandedIds.value.add(id);
  }
}

function toggleAll() {
  const groups = organizationTree.value?.groups || [];
  if (isAllExpanded.value) {
    expandedIds.value.clear();
  } else {
    groups.forEach(g => expandedIds.value.add(g.id));
  }
}

function expandActiveGroup() {
  const groups = organizationTree.value?.groups || [];
  groups.forEach((g, idx) => {
    if (idx < 3 || g.stores?.some(s => s.id === session.storeId)) {
      expandedIds.value.add(g.id);
    }
  });
}

function handleSelectStore(storeId) {
  switchStore(storeId);
  showToast('已切换至目标工作店铺', 'success');
}

async function refreshTree() {
  loading.value = true;
  try {
    await loadOrganizationStores({ force: true });
    expandActiveGroup();
    showToast('组织架构已刷新', 'success');
  } catch (error) {
    showToast('刷新失败，请稍后重试', 'error');
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  expandActiveGroup();
});
</script>

<style scoped>
.members-panel {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-row h1 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--text-dark, #0f172a);
}

.org-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.header-desc {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--text-muted, #64748b);
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid var(--border-color, #cbd5e1);
  background: #ffffff;
  color: var(--text-dark, #0f172a);
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.btn-refresh:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% { transform: rotate(360deg); }
}

.org-tree-card {
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid var(--border-color, #e2e8f0);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.tree-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
}

.search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: min(340px, 100%);
}

.search-icon {
  position: absolute;
  left: 10px;
  color: #94a3b8;
  pointer-events: none;
}

.tree-search-input {
  width: 100%;
  height: 34px;
  padding: 0 28px 0 32px;
  border-radius: 8px;
  border: 1px solid var(--border-color, #cbd5e1);
  font-size: 12.5px;
  outline: none;
  transition: border-color 0.15s ease;
}

.tree-search-input:focus {
  border-color: var(--color-primary);
}

.btn-clear {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  padding: 2px;
}

.btn-toggle-all {
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: var(--text-dark, #0f172a);
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-toggle-all:hover {
  background: #f8fafc;
}

.tree-grid-container {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  background: #f8fafc;
}
</style>
